import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import { money } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const net = (items) => items.reduce((s, r) => s + (r.job_charge ? 0 : r.amount), 0);
const gst = (items) => items.reduce((s, r) => s + (r.job_charge ? 0 : r.gst_amount), 0);

const okDate = (r) => /^\d{4}-\d{2}-\d{2}$/.test(r.date || "");
const groupBy = (items, keyFn) => {
  const m = {};
  items.forEach((r) => (m[keyFn(r)] ||= []).push(r));
  return m;
};

export const GroupedExpenses = ({ rows, selected, setSelected, renderRow }) => {
  const [open, setOpen] = useState(new Set());
  const toggleOpen = (k) => setOpen((s) => {
    const n = new Set(s);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });
  const selectItems = (items, on) => setSelected((s) => {
    const n = new Set(s);
    items.forEach((r) => (on ? n.add(r.id) : n.delete(r.id)));
    return n;
  });

  const GroupRow = ({ k, label, items, depth }) => {
    const count = items.filter((r) => selected.has(r.id)).length;
    const state = count === 0 ? false : count === items.length ? true : "indeterminate";
    const isOpen = open.has(k);
    const tone = ["bg-slate-100 font-semibold text-slate-900", "bg-slate-50 font-medium text-slate-800", "bg-white text-slate-700"][depth];
    return (
      <tr className={`border-b border-slate-100 ${tone}`} data-testid={`group-row-${k}`}>
        <td className="px-5 py-2.5">
          <Checkbox data-testid={`select-group-${k}`} checked={state} onCheckedChange={() => selectItems(items, state !== true)} aria-label={`Select ${label}`} />
        </td>
        <td colSpan={3} className="py-2.5 cursor-pointer select-none" style={{ paddingLeft: 20 + depth * 22 }} onClick={() => toggleOpen(k)} data-testid={`toggle-group-${k}`}>
          <span className="inline-flex items-center gap-1.5">
            <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
            {label}
            <span className="text-xs font-normal text-slate-400 ml-1">{items.length}</span>
          </span>
        </td>
        <td className="px-5 py-2.5 text-right tabular-nums">{money(net(items))}</td>
        <td className="px-5 py-2.5 text-right tabular-nums text-blue-600">{money(gst(items))}</td>
        <td></td>
      </tr>
    );
  };

  const years = groupBy(rows, (r) => (okDate(r) ? r.date.slice(0, 4) : "No date"));
  return Object.keys(years).sort().reverse().map((y) => {
    const yk = `y${y}`;
    const months = groupBy(years[y], (r) => (okDate(r) ? r.date.slice(5, 7) : ""));
    return (
      <Fragment key={yk}>
        <GroupRow k={yk} label={y} items={years[y]} depth={0} />
        {open.has(yk) && Object.keys(months).sort().reverse().map((m) => {
          const mk = `${yk}-m${m}`;
          const vendors = groupBy(months[m], (r) => r.vendor || "(no vendor)");
          return (
            <Fragment key={mk}>
              <GroupRow k={mk} label={MONTHS[parseInt(m, 10) - 1] || "No month"} items={months[m]} depth={1} />
              {open.has(mk) && Object.keys(vendors).sort((a, b) => a.localeCompare(b)).map((v) => {
                const vk = `${mk}-v${v}`;
                return (
                  <Fragment key={vk}>
                    <GroupRow k={vk} label={v} items={vendors[v]} depth={2} />
                    {open.has(vk) && [...vendors[v]].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map(renderRow)}
                  </Fragment>
                );
              })}
            </Fragment>
          );
        })}
      </Fragment>
    );
  });
};
